import React, { useMemo } from "react";
import Select from "react-select";
import ElapsedTimeInput from "./ElapsedTimeInput";
import DropDownStyles, { selectMenuPortalProps } from "./dropdownStyles";
import {
  effortTypeForSamplingMethod,
  requiresEffortUnits,
  isAnglingSamplingMethod,
  durationHoursFromRange,
  formatHMS,
  countFishForEffortUnit,
  buildAnglerFishSummary,
} from "../../utils/samplingCpueUtils";

const readOnlyCountStyle = {
  backgroundColor: "var(--wf-input-bg, #fafafa)",
  fontWeight: 600,
  color: "var(--wf-input-text, #495057)",
  border: "1px solid var(--wf-input-border, #dee2e6)",
};

function buildDefaultUnits(method, count) {
  const effortType = effortTypeForSamplingMethod(method);
  if (!effortType || !count) return [];
  const n = parseInt(count, 10);
  if (!Number.isFinite(n) || n < 1) return [];

  const prefix =
    effortType === "electrofishing_area"
      ? "Area"
      : effortType === "seine_haul"
        ? "Haul"
        : "Net";

  return Array.from({ length: n }, (_, i) => ({
    EffortType: effortType,
    Label: `${prefix} ${i + 1}`,
    SortOrder: i,
    TimeStarted: "",
    TimeStopped: "",
    NetNights: effortType === "seine_haul" ? null : 1,
  }));
}

export function buildDefaultAnglerSlots(count, previous = []) {
  const n = parseInt(count, 10);
  if (!Number.isFinite(n) || n < 1) return [];
  const prev = Array.isArray(previous) ? previous : [];
  return Array.from({ length: n }, (_, i) => {
    const existing = prev[i];
    if (existing) {
      return {
        ...existing,
        label: existing.label || `Angler ${i + 1}`,
        sortOrder: i,
      };
    }
    return {
      id: null,
      label: `Angler ${i + 1}`,
      staffId: null,
      sortOrder: i,
    };
  });
}

const InlineFishCount = ({ value }) => (
  <div style={{ display: "none" }} aria-hidden="true">
    <label className="small text-muted">Fish sampled</label>
    <div
      className="form-control form-control-sm"
      style={readOnlyCountStyle}
      aria-readonly="true"
    >
      {value}
    </div>
  </div>
);

const SamplingEffortFields = ({
  samplingMethod,
  effortCount,
  onEffortCountChange,
  effortUnits,
  onEffortUnitsChange,
  anglerCount,
  onAnglerCountChange,
  fisherStaffOptions = [],
  sessionAnglerSlots = [],
  onSessionAnglerSlotsChange,
  sessionAnglersError,
  fishSamples = [],
  pondAnglers = [],
}) => {
  const effortType = effortTypeForSamplingMethod(samplingMethod);
  const showEffort = requiresEffortUnits(samplingMethod);
  const showAnglers = isAnglingSamplingMethod(samplingMethod);

  const totalElectroHours = useMemo(() => {
    if (effortType !== "electrofishing_area") return 0;
    return (effortUnits || []).reduce(
      (sum, u) => sum + durationHoursFromRange(u.TimeStarted, u.TimeStopped),
      0
    );
  }, [effortUnits, effortType]);

  const anglerSummary = useMemo(
    () => buildAnglerFishSummary(fishSamples, pondAnglers.length ? pondAnglers : sessionAnglerSlots),
    [fishSamples, pondAnglers, sessionAnglerSlots]
  );

  const effortTotals = useMemo(() => {
    const unassigned = (fishSamples || []).filter((fish) => {
      const unitId = fish.EffortUnitID ?? fish.effortUnitID;
      return unitId == null || unitId === "";
    }).length;
    return {
      unassigned,
      total: (fishSamples || []).length,
    };
  }, [fishSamples]);

  const staffOptionsByValue = useMemo(() => {
    const map = new Map();
    (fisherStaffOptions || []).forEach((opt) => map.set(String(opt.value), opt));
    return map;
  }, [fisherStaffOptions]);

  const handleCountChange = (e) => {
    const count = e.target.value;
    onEffortCountChange(count);
    onEffortUnitsChange(buildDefaultUnits(samplingMethod, count));
  };

  const handleAnglerCountChange = (e) => {
    const count = e.target.value;
    if (onAnglerCountChange) onAnglerCountChange(count);
    if (onSessionAnglerSlotsChange) {
      onSessionAnglerSlotsChange(buildDefaultAnglerSlots(count, sessionAnglerSlots));
    }
  };

  const updateAnglerSlot = (index, patch) => {
    if (!onSessionAnglerSlotsChange) return;
    const next = [...(sessionAnglerSlots || [])];
    next[index] = { ...next[index], ...patch };
    onSessionAnglerSlotsChange(next);
  };

  const updateUnit = (index, field, value) => {
    const next = [...(effortUnits || [])];
    next[index] = { ...next[index], [field]: value };
    onEffortUnitsChange(next);
  };

  if (!showEffort && !showAnglers) return null;

  const unitLabel =
    effortType === "seine_haul"
      ? "haul"
      : effortType === "electrofishing_area"
        ? "area"
        : "net";

  const effortUnitColClass =
    effortType === "electrofishing_area"
      ? "col-12 col-lg-6"
      : "col-12 col-md-6 col-xl-4";

  return (
    <div className="col-12 mt-2">
      {showAnglers && (
        <div className="mb-3">
          <div className="row g-3 align-items-start">
            <div className="col-12 col-md-4 col-lg-3">
              <div className="form-floating">
                <input
                  type="number"
                  min="1"
                  className={`form-control ${sessionAnglersError ? "is-invalid" : ""}`}
                  id="numberOfAnglers"
                  value={anglerCount ?? ""}
                  onChange={handleAnglerCountChange}
                />
                <label htmlFor="numberOfAnglers">Number of anglers</label>
              </div>
              {sessionAnglersError && (
                <div className="invalid-feedback d-block">{sessionAnglersError}</div>
              )}
              <p className="small text-muted mb-0 mt-2">
                Enter how many anglers fished, then optionally assign staff and rename each slot.
              </p>
            </div>
          </div>

          {(sessionAnglerSlots || []).length > 0 && (
            <div className="row g-2 mt-2">
              {sessionAnglerSlots.map((slot, index) => {
                const staffValue =
                  slot.staffId != null && slot.staffId !== ""
                    ? staffOptionsByValue.get(String(slot.staffId)) || {
                        value: slot.staffId,
                        label: slot.fullName || slot.label || `Staff ${slot.staffId}`,
                      }
                    : null;
                const summaryRow = anglerSummary.rows.find((row) => {
                  if (slot.id != null && row.sessionAnglerId != null) {
                    return String(row.sessionAnglerId) === String(slot.id);
                  }
                  if (slot.staffId != null && row.staffId != null) {
                    return String(row.staffId) === String(slot.staffId);
                  }
                  return row.label === (slot.label || `Angler ${index + 1}`);
                });
                return (
                  <div key={slot.id ?? `new-${index}`} className="col-12 col-md-6 col-xl-4">
                    <div className="card border-0 donezo-effort-unit-card mb-0 p-3 h-100">
                      <div className="mb-2">
                        <label className="small text-muted">Angler label</label>
                        <input
                          type="text"
                          className="form-control form-control-sm"
                          value={slot.label || ""}
                          onChange={(e) =>
                            updateAnglerSlot(index, { label: e.target.value })
                          }
                          placeholder={`Angler ${index + 1}`}
                        />
                      </div>
                      <div className="mb-2">
                        <label className="small text-muted">Staff (optional)</label>
                        <Select
                          isClearable
                          options={fisherStaffOptions}
                          value={staffValue}
                          onChange={(opt) =>
                            updateAnglerSlot(index, {
                              staffId: opt?.value ?? null,
                              fullName: opt?.label ?? null,
                            })
                          }
                          styles={DropDownStyles}
                          {...selectMenuPortalProps}
                          placeholder="Assign staff…"
                        />
                      </div>
                      <InlineFishCount value={summaryRow?.fishCount ?? 0} />
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {anglerSummary.unassigned > 0 && (
            <div className="d-flex justify-content-between align-items-center py-2 px-2 mb-1 mt-2 rounded donezo-effort-unit-card">
              <span className="small text-muted fst-italic">Unassigned fish</span>
              <InlineFishCount value={anglerSummary.unassigned} />
            </div>
          )}

          {anglerSummary.total > 0 && (
            <p className="small text-muted mb-0 mt-2" style={{ display: "none" }} aria-hidden="true">
              Total fish sampled: <strong>{anglerSummary.total}</strong>
            </p>
          )}

          {(sessionAnglerSlots || []).length > 0 && anglerSummary.total === 0 && (
            <p className="small text-muted mb-0 mt-2">
              No fish samples yet — enter fish on the Fish Samples tab and assign
              each to an angler.
            </p>
          )}
        </div>
      )}

      {showEffort && (
        <>
          <div className="col-12 col-md-6 col-lg-4">
            <div className="form-floating mb-3">
              <input
                type="number"
                min="1"
                className="form-control"
                id="effortUnitCount"
                value={effortCount}
                onChange={handleCountChange}
              />
              <label htmlFor="effortUnitCount">
                {effortType === "electrofishing_area"
                  ? "Number of electrofished areas"
                  : effortType === "seine_haul"
                    ? "Number of seine hauls"
                    : "Number of nets"}
              </label>
            </div>
          </div>

          <div className="col-12">
            <div className="row g-2">
          {(effortUnits || []).map((unit, index) => {
            const unitFishCount =
              unit.id != null ? countFishForEffortUnit(fishSamples, unit.id) : 0;

            return (
              <div key={index} className={effortUnitColClass}>
              <div className="card border-0 donezo-effort-unit-card mb-0 p-3 h-100">
                <div className="fw-semibold small mb-2">{unit.Label}</div>

                {effortType === "electrofishing_area" ? (
                  <div className="row g-2 align-items-end">
                    <div className="col-md-6">
                      <label className="small text-muted">Start (HH:MM:SS)</label>
                      <ElapsedTimeInput
                        className="form-control form-control-sm"
                        placeholder="00:00:00"
                        value={unit.TimeStarted}
                        onChange={(e) =>
                          updateUnit(index, "TimeStarted", e.target.value)
                        }
                      />
                    </div>
                    <div className="col-md-6">
                      <label className="small text-muted">Stop (HH:MM:SS)</label>
                      <ElapsedTimeInput
                        className="form-control form-control-sm"
                        placeholder="00:14:00"
                        value={unit.TimeStopped}
                        onChange={(e) =>
                          updateUnit(index, "TimeStopped", e.target.value)
                        }
                      />
                    </div>
                    <div className="col-md-3" style={{ display: "none" }} aria-hidden="true">
                      <label className="small text-muted">Duration</label>
                      <div
                        className="form-control form-control-sm donezo-effort-readonly"
                        aria-readonly="true"
                      >
                        {formatHMS(
                          durationHoursFromRange(
                            unit.TimeStarted,
                            unit.TimeStopped
                          )
                        )}
                      </div>
                    </div>
                    <div className="col-md-3" style={{ display: "none" }} aria-hidden="true">
                      <InlineFishCount value={unitFishCount} />
                    </div>
                  </div>
                ) : effortType === "seine_haul" ? (
                  <div className="row g-2 align-items-end">
                    <div className="col-md-8">
                      <p className="small text-muted mb-0">1 haul = 1 effort unit</p>
                    </div>
                    <div className="col-md-4">
                      <InlineFishCount value={unitFishCount} />
                    </div>
                  </div>
                ) : (
                  <div className="row g-2 align-items-end">
                    <div className="col-6">
                      <label className="small text-muted">Number of nights</label>
                      <input
                        type="number"
                        min="1"
                        className="form-control form-control-sm"
                        value={unit.NetNights ?? 1}
                        onChange={(e) =>
                          updateUnit(index, "NetNights", e.target.value)
                        }
                      />
                    </div>
                    <div className="col-6">
                      <InlineFishCount value={unitFishCount} />
                    </div>
                  </div>
                )}
              </div>
              </div>
            );
          })}
            </div>
          </div>

          {effortType === "electrofishing_area" && totalElectroHours > 0 && (
            <p className="small text-muted">
              Total sampling time:{" "}
              <strong>{totalElectroHours.toFixed(3)} hrs</strong>
            </p>
          )}

          {effortTotals.unassigned > 0 && (
            <p className="small text-muted mb-1">
              Unassigned fish: <strong>{effortTotals.unassigned}</strong>
            </p>
          )}
          {effortTotals.total > 0 && (
            <p className="small text-muted mb-1" style={{ display: "none" }} aria-hidden="true">
              Total fish sampled: <strong>{effortTotals.total}</strong>
            </p>
          )}

          <p className="small text-muted mb-0 mt-2">
            Enter each sampled fish on the <strong>Fish Samples</strong> tab and
            assign it to the correct {unitLabel} for CPUE. Fish counts update
            automatically.
          </p>
        </>
      )}
    </div>
  );
};

export { buildDefaultUnits };
export default SamplingEffortFields;
